/* ═══════════════════════════════════════════════════
   TrackLab v2 — Schedule Polling & Race Lifecycle
   Core orchestrator: polls /api/live/schedule/ and
   reconciles DOM state (add/remove/promote races).
   ═══════════════════════════════════════════════════ */

(function () {
  'use strict';

  var SCHEDULE_INTERVAL = 30000;   // 30s normal
  var SCHEDULE_BACKOFF  = 60000;   // 60s on failure
  var CONN_FAIL_THRESHOLD = 3;     // show indicator after N failures
  var LINGER_MS = 300000;          // 5 min max linger for finished races

  var scheduleTimer = null;
  var scheduleFailures = 0;
  var knownRaceIds = {};           // raceId → 'live' | 'upcoming' | 'lingering'
  var lingerTimers = {};           // raceId → setTimeout handle
  var cardFetchInFlight = {};      // raceId → true (prevents duplicate fetches)
  var userHasInteracted = false;   // true once user clicks a banner — suppresses auto-expand
  var isUpcomingPage = false;      // true on the upcoming page — disables promote/remove

  TL.schedule = {};

  TL.schedule.userInteracted = function () {
    userHasInteracted = true;
  };

  // ── Init ──

  TL.schedule.init = function () {
    // Detect page type — upcoming page has no #v2LiveSection
    isUpcomingPage = !document.getElementById('v2LiveSection');

    // Don't poll schedule on tomorrow's page — the endpoint returns
    // today's data and race ID collisions cause false "Finished" badges
    var page = document.querySelector('.v2-page');
    if (page && page.dataset.tomorrow) {
      TL.countdown.update();
      return;
    }

    // Scan DOM for existing races
    document.querySelectorAll('.live-banner[data-race-id]').forEach(function (b) {
      var id = b.dataset.raceId;
      knownRaceIds[id] = 'live';
      // Start tier-2 polling for existing live banners
      var eventId = b.dataset.eventId;
      if (eventId) TL.polling.start(id, eventId);
      // Lock off_dt if already in template
      if (b.dataset.offTime && TL.state.activePollers[id]) {
        TL.state.activePollers[id].offDtLocked = true;
      }
    });

    document.querySelectorAll('.race-row[data-race-id]').forEach(function (r) {
      knownRaceIds[r.dataset.raceId] = 'upcoming';
    });

    // Start countdown & progress tickers
    TL.countdown.update();
    TL.progress.updateBanners();

    // Start schedule polling
    scheduleTimer = setInterval(function () {
      TL.schedule.poll();
    }, SCHEDULE_INTERVAL);

    // Immediate catch-up when tab regains focus
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        TL.schedule.poll();
      }
    });
  };

  // ── Schedule Poll ──

  TL.schedule.poll = function () {
    // Skip if tab is hidden
    if (document.hidden) return;

    fetch('/api/live/schedule/')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        scheduleFailures = 0;
        _hideConnLost();
        _handleScheduleUpdate(data);
      })
      .catch(function () {
        scheduleFailures++;
        if (scheduleFailures >= CONN_FAIL_THRESHOLD) {
          _showConnLost();
        }
      });
  };

  // ── Reconciliation ──

  function _handleScheduleUpdate(data) {
    var serverLiveIds = {};
    var serverUpNextIds = {};
    var serverFinishedSet = {};

    // Index server data
    (data.live_races || []).forEach(function (r) { serverLiveIds[r.id] = r; });
    (data.up_next || []).forEach(function (r) { serverUpNextIds[r.id] = r; });
    (data.finished_ids || []).forEach(function (id) { serverFinishedSet[id] = true; });

    // A. Handle finished races
    Object.keys(serverFinishedSet).forEach(function (id) {
      if (knownRaceIds[id] && knownRaceIds[id] !== 'lingering') {
        if (isUpcomingPage) {
          // On upcoming page, just badge the race row — don't remove or linger
          TL.views.markRaceRowFinished(id);
        } else {
          TL.schedule.handleRaceFinished(id);
        }
      }
    });

    // B. Handle live races
    Object.keys(serverLiveIds).forEach(function (id) {
      var raceData = serverLiveIds[id];

      if (isUpcomingPage) {
        // On upcoming page, badge the race row in-place — never promote
        if (knownRaceIds[id]) {
          TL.views.markRaceRowLive(id);
        }
        // Don't fetch or insert new races that weren't in the SSR
      } else if (knownRaceIds[id] === 'live') {
        // Already a live banner — update data attributes if changed
        _updateBannerData(id, raceData);
        // Its card failed to load last time: try again
        if (_cardError(id)) _loadLiveCard(id, raceData);
        // If is_awaiting, show awaiting state
        if (raceData.is_awaiting) {
          TL.views.showAwaitingState(id);
        }
      } else if (knownRaceIds[id] === 'upcoming') {
        // Currently in up-next — promote to live
        _promoteToLive(id, raceData);
      } else if (!knownRaceIds[id]) {
        // Completely new — fetch card then promote
        _fetchAndPromote(id, raceData);
      }
    });

    // C. Handle up-next races
    Object.keys(serverUpNextIds).forEach(function (id) {
      if (!knownRaceIds[id] && !isUpcomingPage) {
        // New race entered the window — fetch card and insert (live page only)
        _fetchAndInsertUpNext(id, serverUpNextIds[id]);
      }
      // If already known as upcoming, no-op (countdown.js handles timer)
    });

    // D. Handle races no longer in any server list
    if (!isUpcomingPage) {
      Object.keys(knownRaceIds).forEach(function (id) {
        if (knownRaceIds[id] === 'lingering') return; // managed by linger timer
        if (!serverLiveIds[id] && !serverUpNextIds[id] && !serverFinishedSet[id]) {
          // Race disappeared from server response
          _removeRace(id);
        }
      });
    }

    // E. Determine which live race should be expanded (newest by race_start_iso)
    if (!isUpcomingPage) {
      _updatePromotedRace(serverLiveIds);
    }

    // F. Empty state check (refresh the later-today count first so an
    // emptied Up Next only says "No more races today" when it's true)
    if (!isUpcomingPage) {
      if (typeof data.later_count === 'number') {
        TL.views.setLaterState(data.later_count, data.next_later);
      }
      if ('next_race' in data) TL.views.setNextRace(data.next_race);
      _checkEmptyState();
    }

    // G. Update up-next count
    TL.views.updateUpNextCount();
  }

  // ── Race Finished Handler (called by polling.js or schedule) ──

  TL.schedule.handleRaceFinished = function (raceId) {
    if (knownRaceIds[raceId] === 'lingering') return; // already lingering

    // Show awaiting state
    TL.views.showAwaitingState(raceId);

    // Stop tier-2 polling for this race
    TL.polling.stop(raceId);

    knownRaceIds[raceId] = 'lingering';

    // Start linger timer — remove after LINGER_MS
    if (lingerTimers[raceId]) clearTimeout(lingerTimers[raceId]);
    lingerTimers[raceId] = setTimeout(function () {
      _removeRace(raceId);
      delete lingerTimers[raceId];
      _checkEmptyState();
      TL.views.updateUpNextCount();
    }, LINGER_MS);
  };

  // ── Internal Helpers ──

  function _updateBannerData(raceId, raceData) {
    var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
    if (!banner) return;
    if (raceData.off_dt_iso && !banner.dataset.offTime) {
      banner.dataset.offTime = raceData.off_dt_iso;
    }
    if (raceData.race_duration_seconds) {
      banner.dataset.duration = raceData.race_duration_seconds;
    }
  }

  function _promoteToLive(raceId, raceData) {
    // Reset interaction flag so new race auto-expands
    userHasInteracted = false;
    // Remove from up-next
    TL.views.removeRaceRow(raceId);

    // Build and insert live banner
    var bannerHtml = TL.views.buildLiveBannerFromData(raceData);
    if (bannerHtml) {
      TL.views.insertLiveBanner(bannerHtml);
    }

    // Fetch full card data for the inline detail (runners, etc.)
    _loadLiveCard(raceId, raceData);

    // Start tier-2 polling
    if (raceData.event_id) {
      TL.polling.start(raceId, raceData.event_id);
    }

    knownRaceIds[raceId] = 'live';
  }

  function _fetchAndPromote(raceId, raceData) {
    if (cardFetchInFlight[raceId]) return;
    userHasInteracted = false; // auto-expand new race

    // Build banner from schedule data
    var bannerHtml = TL.views.buildLiveBannerFromData(raceData);
    if (bannerHtml) {
      TL.views.insertLiveBanner(bannerHtml);
    }

    // Fetch full card for inline detail
    _loadLiveCard(raceId, raceData);
    if (raceData.event_id) {
      TL.polling.start(raceId, raceData.event_id);
    }

    knownRaceIds[raceId] = 'live';
  }

  function _fetchAndInsertUpNext(raceId, raceData) {
    if (cardFetchInFlight[raceId]) return;
    cardFetchInFlight[raceId] = true;

    // A skeleton row holds the race's slot while its card loads; on failure
    // it goes quietly and the next schedule poll tries again.
    TL.skeleton.load(_fetchCard(raceData.event_id), {
      busy: document.getElementById('v2UpNextRaces'),
      label: 'Loading race',
      show: function () {
        return TL.views.insertRaceRow(TL.skeleton.rowHTML(), raceData.race_start_iso);
      }
    })
      .then(function (res) {
        TL.views.insertRaceRow(res.value, raceData.race_start_iso);
        TL.skeleton.remove(res.skeleton);
        knownRaceIds[raceId] = 'upcoming';
        TL.views.updateUpNextCount();
      }, function (res) {
        TL.skeleton.remove(res.skeleton);
        _checkEmptyState();
        console.warn('[TrackLab] Failed to fetch card for up-next race ' + raceId);
      })
      .finally(function () {
        delete cardFetchInFlight[raceId];
      });
  }

  function _fetchCard(eventId) {
    return fetch('/api/race/' + eventId + '/card/').then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    });
  }

  function _cardError(raceId) {
    return document.querySelector('.tl-skel-error[data-race-id="' + raceId + '"]');
  }

  // Put a node straight after the race's live banner.
  function _placeAfterBanner(raceId, node) {
    var section = document.getElementById('v2LiveSection');
    var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
    if (section && banner && banner.nextSibling) {
      section.insertBefore(node, banner.nextSibling);
    } else if (section) {
      section.appendChild(node);
    }
    return node;
  }

  /**
   * Fetch a live race's card and put its inline detail under the banner.
   * While it loads, a placeholder card holds the place (only when the card
   * will open on arrival). It has one row per runner (from the schedule data),
   * so the page below hardly moves. On an error or after 8s the placeholder
   * becomes a one-line message; the next schedule poll retries, and the
   * message goes when the card arrives.
   */
  function _loadLiveCard(raceId, raceData) {
    if (!raceData.event_id || cardFetchInFlight[raceId]) return;
    cardFetchInFlight[raceId] = true;
    var error = _cardError(raceId);

    TL.skeleton.load(_fetchCard(raceData.event_id), {
      busy: document.getElementById('v2LiveSection'),
      label: 'Loading race',
      show: function () {
        if (error || !_canAutoExpand(raceId)) return null;
        return _placeAfterBanner(raceId, TL.skeleton.node(TL.skeleton.cardHTML(raceData.number_of_runners)));
      }
    })
      .then(function (res) {
        var temp = document.createElement('div');
        temp.innerHTML = res.value;
        var detail = temp.querySelector('.race-inline-detail');
        var slot = res.skeleton || error;
        if (!detail) {
          TL.skeleton.remove(slot);
          return;
        }
        // Swap an open placeholder for an already open card, so it doesn't
        // collapse and animate open again.
        if (res.skeleton && res.skeleton.classList.contains('open') && _canAutoExpand(raceId)) {
          detail.classList.add('open');
        }
        if (slot && slot.parentNode) slot.parentNode.replaceChild(detail, slot);
        else _placeAfterBanner(raceId, detail);
        TL.skeleton.remove(error);
        // Auto-expand now that the detail is in the DOM
        var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
        _autoExpandAfterCardLoad(raceId, banner, detail);
      }, function (res) {
        console.warn('[TrackLab] Failed to fetch card for live race ' + raceId);
        if (_cardError(raceId)) return TL.skeleton.remove(res.skeleton);
        var msg = TL.skeleton.node('<p class="tl-skel-error"></p>');
        msg.dataset.raceId = raceId;
        msg.textContent = 'Couldn\u2019t load this race. Retrying\u2026';
        if (res.skeleton && res.skeleton.parentNode) res.skeleton.parentNode.replaceChild(msg, res.skeleton);
        else _placeAfterBanner(raceId, msg);
      })
      .finally(function () {
        delete cardFetchInFlight[raceId];
      });
  }

  function _removeRace(raceId) {
    var state = knownRaceIds[raceId];
    if (!state) return;

    if (state === 'live' || state === 'lingering') {
      TL.views.removeLiveBanner(raceId);
      TL.skeleton.remove(_cardError(raceId));
      TL.polling.stop(raceId);
    } else if (state === 'upcoming') {
      TL.views.removeRaceRow(raceId);
    }

    delete knownRaceIds[raceId];
    if (lingerTimers[raceId]) {
      clearTimeout(lingerTimers[raceId]);
      delete lingerTimers[raceId];
    }
  }

  /**
   * Ensure the newest live race is expanded and others are collapsed.
   */
  function _updatePromotedRace(serverLiveIds) {
    var liveBanners = document.querySelectorAll('.live-banner:not(.awaiting)');
    if (liveBanners.length === 0) return;

    // Find newest by race_start_iso
    var newest = null;
    var newestTime = 0;
    liveBanners.forEach(function (b) {
      var t = b.dataset.raceStart ? new Date(b.dataset.raceStart).getTime() : 0;
      if (t > newestTime) {
        newestTime = t;
        newest = b;
      }
    });

    // Collapse non-newest banners (only if multiple)
    liveBanners.forEach(function (b) {
      if (b !== newest) {
        b.classList.add('collapsed');
        b.classList.remove('expanded');
        var detail = document.getElementById('v2InlineDetail-' + b.dataset.raceId);
        if (detail) detail.classList.remove('open');
      }
    });

    if (newest) {
      newest.classList.remove('collapsed');
      // Only auto-expand if: user hasn't interacted, nothing already expanded,
      // AND the inline detail exists in the DOM (card fetch has completed)
      var anyExpanded = document.querySelector('.live-banner.expanded');
      if (!anyExpanded && !userHasInteracted) {
        var newestId = newest.dataset.raceId;
        var detail = document.getElementById('v2InlineDetail-' + newestId);
        if (detail) {
          newest.classList.add('expanded');
          detail.classList.add('open');
          _ensureTrackElements(detail);
          if (TL.track) TL.track.start();
          var firstRunner = detail.querySelector('.runner');
          if (firstRunner && !detail.querySelector('.runner.sel')) {
            TL.runner.selectRunner(firstRunner, newestId);
          }
        }
        // If detail doesn't exist yet, _autoExpandAfterCardLoad will handle it
      }
    }
  }

  function _checkEmptyState() {
    var liveBanners = document.querySelectorAll('.live-banner');
    var upNextRows = document.querySelectorAll('#v2UpNextRaces .race-row');
    if (liveBanners.length === 0 && upNextRows.length === 0) {
      TL.views.showEmptyState();
    } else {
      TL.views.hideEmptyState();
    }
  }

  // A new live card opens by itself unless the user has tapped a banner or
  // another banner is open (this one may already be, via _updatePromotedRace).
  function _canAutoExpand(raceId) {
    return !userHasInteracted &&
      !document.querySelector('.live-banner.expanded:not([data-race-id="' + raceId + '"])');
  }

  /**
   * Auto-expand a banner + detail after the card HTML has loaded.
   * Called from the card fetch .then() callback once the detail is in the DOM.
   */
  function _autoExpandAfterCardLoad(raceId, banner, detail) {
    if (!banner || !detail || !_canAutoExpand(raceId)) return;

    banner.classList.add('expanded');
    detail.classList.add('open');
    _ensureTrackElements(detail);
    if (TL.track) TL.track.start();
    // Auto-select first runner
    var firstRunner = detail.querySelector('.runner');
    if (firstRunner && !detail.querySelector('.runner.sel')) {
      TL.runner.selectRunner(firstRunner, raceId);
    }
  }

  /**
   * Ensure the singleton track elements (v2Trk, v2Horse, etc.) exist inside
   * the given inline detail. If they're in a different detail, move them.
   * If they don't exist at all (dynamically promoted race), create them.
   */
  function _ensureTrackElements(detail) {
    var trk = document.getElementById('v2Trk');
    // Insert track after the header (rid-header is a direct child of detail;
    // rid-title is nested inside it and isn't a valid insertBefore reference).
    var header = detail.querySelector(':scope > .rid-header');
    var insertRef = header ? header.nextSibling : detail.firstChild;

    if (trk) {
      // Track elements exist — move them if not already in this detail
      if (!detail.contains(trk)) {
        detail.insertBefore(trk, insertRef);
      }
      return;
    }
    // Track elements don't exist (page had no live race on SSR) — create them
    var html = '<div class="v2-trk" id="v2Trk">'
      + '<div class="v2-tr-r v2-tr-t"></div>'
      + '<div class="v2-tr-r v2-tr-b"></div>'
      + '<div class="v2-tr-g"><div class="v2-gs"></div><div class="v2-gs"></div><div class="v2-gs"></div><div class="v2-gs"></div></div>'
      + '<div class="v2-tr-f"></div>'
      + '<div class="v2-trk-sw" id="v2TrkSw"></div>'
      + '<div class="v2-trk-tx" id="v2TrkTx">They\'re off!</div>'
      + '<div class="v2-trk-walk" id="v2TrkWalk">Heading to the start</div>'
      + '<div class="v2-horse idle" id="v2Horse" style="left:5%;" data-anim-path="/static/racing/animations/horse.json"></div>'
      + '<div class="v2-dust-wrap" id="v2Dust"><div class="v2-dp"></div><div class="v2-dp"></div><div class="v2-dp"></div></div>'
      + '<div class="v2-ghost-horses" id="v2Ghosts"></div>'
      + '</div>';
    var temp = document.createElement('div');
    temp.innerHTML = html;
    detail.insertBefore(temp.firstChild, insertRef);
  }

  // ── Connection Indicator ──

  function _showConnLost() {
    var el = document.getElementById('v2ConnLost');
    if (el) el.classList.remove('hidden');
  }

  function _hideConnLost() {
    var el = document.getElementById('v2ConnLost');
    if (el) el.classList.add('hidden');
  }
})();
